import type { ComponentType } from "react";
import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { useMount } from "app/custom-hooks/useMount";
import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { toggleNotFoundListModalOpen } from "app/pages/sales-intelligence/sub-modules/common/store/action-creators";
import { fetchWorkspacesIfMissingOpportunityListsThunk } from "app/pages/sales-intelligence/sub-modules/common/store/effects";
import { fetchCrmListsIfMissingThunk } from "app/pages/sales-intelligence/sub-modules/management/store/effects/crmLists";
import {
    selectHasOpportunityListById,
    selectIsFetchingOpportunityLists,
} from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";
import type { UnknownProps } from "app/pages/sales-intelligence/types";

function withNotFoundListGuard<P extends UnknownProps>(ListPageComponent: ComponentType<P>): ComponentType<P> {
    return function WrappedWithNotFoundListGuard(props: P) {
        const dispatch = useDispatch();

        const { id: currentListId } = useListParams();

        const hasList = useSelector((state) => selectHasOpportunityListById(state, currentListId));

        const isFetchingOpportunityLists = useSelector(selectIsFetchingOpportunityLists);

        const [isReady, setIsReady] = useState(hasList);

        useMount(() => {
            void dispatch(fetchWorkspacesIfMissingOpportunityListsThunk());
            void dispatch(fetchCrmListsIfMissingThunk());
        });

        useActionAfterFlagChange(isFetchingOpportunityLists, () => {
            if (!hasList) {
                void siNavigator.goToListsHomePage({ location: "replace" });

                dispatch(toggleNotFoundListModalOpen(true));
            } else {
                setIsReady(true);
            }
        });

        if (!isReady) {
            return null;
        }

        return <ListPageComponent {...props} />;
    };
}

export default withNotFoundListGuard;
